import { inject, Injectable } from '@angular/core';
import { FormID } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  FileDocumentResponse,
  NominatedAccountInfoDetailType,
  NominatedAccountStatusEnum,
  PaginatedResponse,
  PaginationMeta,
  SavingsAccountInfoType,
  SentInvitationSummary,
  ViewType,
} from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, State, StateContext, Store, createSelector } from '@ngxs/store';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { SetFormActionWithId } from '../actions/form-submission.action';
import { AddProgressBarStack, RemoveProgressBarStack } from '../actions/progress-bar.action';
import {
  ClearSavingAccountCache,
  CreateSavingsAccount,
  FetchJointAccountRequests,
  GetAccountRequests,
  GetNominatedAccountDetails,
  GetNominatedAccountStatus,
  GetSavingAccount,
  GetSavingAccountDocumentDetails,
  GetSupportedAispInstitutions,
  GetSupportedPispInstitutions,
  RefreshActiveAccountDetails,
  SetPispDepositInitiateResponse,
  SetSavingAccountState,
  SetSavingLoadingStatus,
  SetSupportedPispInstitutions,
  UpdateActiveSavingsAccountId,
  UpdateInternalTransferFromAccountId,
  UpdateInternalTransferToAccountId,
  UpdateSavingAccountName,
  UpdateSavingAccountNameInFormState,
} from '../actions/savings-account.action';
import { UpdateAccountResponse } from '../services/account-service/account.type';
import { DocumentService } from '../services/document-service/document.service';
import { WidgetsHideConditionsSelector } from '../services/hide-conditions-service/hide-conditions-repository';
import { HideConditionsService } from '../services/hide-conditions-service/hide-conditions.service';
import { InvitationService } from '../services/invitation-service/invitation.service';
import { SavingsAccountService } from '../services/savings-account-service/savings-account.service';
import {
  AccountOpenRequestType,
  NominatedAccountStatus,
  SavingsAccountInfoTypeResponse,
  PispSupportedInstitution,
  SupportedInstitution,
  PispDepositInitiateResponse,
} from '../services/savings-account-service/savings-account.type';
import { ApiActions, UpdateApiIsLoadingAction } from './api-loading.state';

export interface SavingsAccountStateModel {
  accounts: SavingsAccountInfoType[];
  accountActivePage: number;
  accountPageLimit: number;
  accountTotalPages: number;
  totalItems: number;
  totalAccountRequests: number;
  activeAccountId: string;
  isLoading: boolean;
  nominatedAccountStatus: NominatedAccountStatus['status'];
  nominatedAccountId: NominatedAccountStatus['accountId'];
  supportedInstitutions: SupportedInstitution[];
  pispSupportedInstitutions: PispSupportedInstitution[];
  pispDepositInitiateResponse?: PispDepositInitiateResponse;
  nominatedAccount: NominatedAccountInfoDetailType | null;
  internalTransferFromAccountId?: string;
  internalTransferToAccountId?: string;
  documentDetails?: FileDocumentResponse[];
  accountRequests?: AccountOpenRequestType[];
  pagination: PaginationMeta;
  latestAccountRequest?: SavingsAccountInfoTypeResponse | null;
  jointAccountRequests?: {
    pendingRequests: SentInvitationSummary[];
    selectedJointAccountRequest: SentInvitationSummary;
  };
}

@State<SavingsAccountStateModel>({
  name: 'savingsAccount',
  defaults: {
    accounts: [],
    activeAccountId: '',
    accountPageLimit: 10,
    accountActivePage: 0,
    accountTotalPages: 0,
    totalItems: 0,
    totalAccountRequests: 0,
    isLoading: false,
    nominatedAccountStatus: NominatedAccountStatusEnum.NOT_CONNECTED,
    nominatedAccountId: '',
    supportedInstitutions: [],
    pispSupportedInstitutions: [],
    pispDepositInitiateResponse: undefined,
    nominatedAccount: null,
    documentDetails: [],
    accountRequests: [],
    pagination: {} as PaginationMeta,
  },
})
@Injectable()
export class SavingsAccountState {
  private savingsAccountService: SavingsAccountService = inject(SavingsAccountService);
  private invitationService: InvitationService = inject(InvitationService);
  private alertHandlerService: AlertHandlerService = inject(AlertHandlerService);
  private hideConditionsService: HideConditionsService = inject(HideConditionsService);
  private documentService: DocumentService = inject(DocumentService);

  @Selector()
  static getAccounts(state: SavingsAccountStateModel) {
    return state;
  }

  @Selector()
  static getCurrentAccountId(state: SavingsAccountStateModel) {
    return state.activeAccountId;
  }

  @Selector()
  static getCurrentAccount(state: SavingsAccountStateModel) {
    return state.accounts?.find((account) => account.accountId === state.activeAccountId);
  }

  @Selector()
  static getNominatedAccountDetails(state: SavingsAccountStateModel) {
    return state.nominatedAccount;
  }

  static getCurrentAccountDetails(activeAccountId: string) {
    return createSelector([SavingsAccountState], (state: SavingsAccountStateModel) => {
      return state.accounts?.find((account) => account.accountId === activeAccountId);
    });
  }

  @Selector()
  static getLoadingStatus(state: SavingsAccountStateModel) {
    return state.isLoading;
  }

  @Selector()
  static getAccountRequests(state: SavingsAccountStateModel) {
    return state.accountRequests;
  }

  @Action(SetSavingLoadingStatus)
  setLoadingStatus(ctx: StateContext<SavingsAccountStateModel>, action: SetSavingLoadingStatus) {
    try {
      ctx.patchState({
        ...ctx.getState(),
        isLoading: action.isLoading,
      });
      return true;
    } catch (err) {
      return throwError(() => err);
    }
  }

  @Action(SetSavingAccountState)
  setAccountState(ctx: StateContext<SavingsAccountStateModel>, action: SetSavingAccountState) {
    ctx.setState(action.state);
  }

  @Action(ClearSavingAccountCache)
  clearAccountCache(ctx: StateContext<SavingsAccountStateModel>) {
    ctx.patchState({
      accounts: [],
      activeAccountId: '',
      accountPageLimit: 10,
      accountActivePage: 0,
      accountTotalPages: 0,
      totalItems: 0,
      isLoading: false,
    });
  }

  @Action(GetSavingAccount)
  fetchAccount(ctx: StateContext<SavingsAccountStateModel>, action: GetSavingAccount) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'GetSavingAccount' }));
    ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getSavingsAccounts, true));

    let params = {
      page: action.page,
      size: action.limit,
      status: action.status,
    };
    if (action.searchParams) {
      params = { ...params, ...action.searchParams };
    }

    return this.savingsAccountService.getSavingAccounts(params).pipe(
      tap((accountsResult: PaginatedResponse<SavingsAccountInfoType>) => {
        const prevData = ctx.getState().accounts;
        let accountPrevResult;

        if (action.viewType === ViewType.TABLE) {
          accountPrevResult = accountsResult.result;
        } else {
          if (action.page === 1) {
            accountPrevResult = accountsResult.result;
          } else {
            accountPrevResult = prevData.concat(accountsResult.result);
          }
        }

        const currentId = ctx.getState().activeAccountId;
        const selectedAccountExistsInApiResponse = accountsResult?.result?.some(
          (account) => account.accountId === currentId,
        );
        const selectedAccountExistsInState = prevData?.some((account) => account.accountId === currentId);

        let activeAccountIdToAssign = currentId;

        // If no account is selected, select the first account
        if (!currentId) {
          activeAccountIdToAssign = accountsResult?.result?.[0]?.accountId;
        }

        // If the selected account is not in the API response, but was in the state, add it back
        if (currentId && selectedAccountExistsInState && !selectedAccountExistsInApiResponse) {
          const activeAccountIndex = prevData.findIndex((account) => account.accountId === currentId);
          if (action.page === 1) accountPrevResult.push(prevData[activeAccountIndex]);
          activeAccountIdToAssign = currentId;
        }

        // If the selected account is in the API response, use it
        if (currentId && selectedAccountExistsInApiResponse) {
          activeAccountIdToAssign = currentId;
        }

        ctx.patchState({
          accounts: accountPrevResult,
          accountActivePage: accountsResult.page,
          accountPageLimit: accountsResult.limit,
          accountTotalPages: accountsResult.totalPages,
          totalItems: accountsResult.totalItems,
          activeAccountId: activeAccountIdToAssign,
          isLoading: false,
          pagination: {
            page: action.page ?? 0,
            totalPages: accountsResult.totalPages ?? 0,
            totalItems: accountsResult.totalItems ?? 0,
            pageSize: accountsResult.limit ?? 10,
          },
        });

        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getSavingsAccounts, false));
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetSavingAccount' }));
      }),
      catchError<unknown, Observable<boolean>>((_err) => {
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getSavingsAccounts, false));
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetSavingAccount' }));
        throw _err;
      }),
    );
  }

  @Action(UpdateActiveSavingsAccountId)
  updateCurrentAccountId(ctx: StateContext<SavingsAccountStateModel>, action: UpdateActiveSavingsAccountId) {
    ctx.dispatch(new UpdateSavingAccountNameInFormState(action.activeAccountId));
    ctx.patchState({
      ...ctx.getState(),
      activeAccountId: action.activeAccountId,
    });
  }

  @Action(UpdateSavingAccountNameInFormState)
  updateSavingAccountNameInFormState(
    ctx: StateContext<SavingsAccountStateModel>,
    action: UpdateSavingAccountNameInFormState,
  ) {
    const state = ctx.getState();
    const matchedAccount = state.accounts?.find((account) => account.accountId === action?.accountId);

    ctx.dispatch(
      new SetFormActionWithId(
        {
          type: '',
          formData: {
            accountNickname: matchedAccount?.accountName,
          },
        },
        FormID.ACCOUNT_NAME_FORM,
      ),
    );
  }

  @Action(UpdateSavingAccountName)
  updateAccountName(ctx: StateContext<SavingsAccountStateModel>, action: UpdateSavingAccountName) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'UpdateSavingAccountName' }));

    return this.savingsAccountService.updateSavingAccount(action.accountId, { name: action.name }).pipe(
      tap((response: UpdateAccountResponse) => {
        if (response) ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'UpdateSavingAccountName' }));
      }),
      catchError<unknown, Observable<boolean>>((_err) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'UpdateSavingAccountName' }));
        throw _err;
      }),
    );
  }

  @Action(FetchJointAccountRequests)
  fetchJointAccountRequests(ctx: StateContext<SavingsAccountStateModel>) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'FetchJointAccountRequests' }));
    ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getJointAccountRequests, true));

    return this.invitationService.getSentInvitations().pipe(
      tap((response) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'FetchJointAccountRequests' }));
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getJointAccountRequests, false));
        const selectedRequest = ctx.getState()?.jointAccountRequests?.selectedJointAccountRequest;
        ctx.patchState({
          ...ctx.getState(),
          jointAccountRequests: {
            pendingRequests: response ?? [],
            selectedJointAccountRequest: selectedRequest ?? response?.[0],
          },
        });
      }),
      catchError<unknown, Observable<boolean>>((_err) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'FetchJointAccountRequests' }));
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getJointAccountRequests, false));
        throw _err;
      }),
    );
  }

  @Selector()
  static getNominatedAccountStatus(state: SavingsAccountStateModel) {
    return state.nominatedAccountStatus;
  }

  @Selector()
  static getNominatedAccountId(state: SavingsAccountStateModel) {
    return state.nominatedAccountId;
  }

  @Selector()
  static getSupportedInstitutions(state: SavingsAccountStateModel) {
    return state.supportedInstitutions;
  }

  @Selector()
  static getPispSupportedInstitutions(state: SavingsAccountStateModel) {
    return state.pispSupportedInstitutions;
  }

  @Selector()
  static getPendingJointAccountRequests(state: SavingsAccountStateModel) {
    return state.jointAccountRequests;
  }

  @Action(GetNominatedAccountStatus)
  fetchNominatedAccountStatus(ctx: StateContext<SavingsAccountStateModel>) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'GetNominatedAccountStatus' }));
    return this.savingsAccountService.fetchNominatedAccountStatus().pipe(
      tap((response) => {
        ctx.patchState({
          nominatedAccountStatus: response.status,
          nominatedAccountId: response.accountId || '',
        });
        const nominatedAccountExists = Object.keys(ctx.getState().nominatedAccount || {}).length;
        if (response.accountId && !nominatedAccountExists)
          ctx.dispatch(new GetNominatedAccountDetails(response.accountId));
        if (response) ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetNominatedAccountStatus' }));
      }),
      catchError<unknown, Observable<boolean>>((_err) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetNominatedAccountStatus' }));
        this.alertHandlerService.showAlertFn('error', 'Failed to get nominated account status');
        throw _err;
      }),
    );
  }

  @Action(GetSupportedAispInstitutions)
  fetchSupportedAispInstitutions(
    ctx: StateContext<SavingsAccountStateModel>,
    action: GetSupportedAispInstitutions,
  ) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'GetSupportedAispInstitutions' }));
    ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.supportedAispInstitutionsLoading, true));
    return this.savingsAccountService.fetchSupportedAispInstitutions(action.country).pipe(
      tap((response) => {
        ctx.patchState({
          supportedInstitutions: response,
        });
        if (response) ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetSupportedAispInstitutions' }));
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.supportedAispInstitutionsLoading, false));
      }),
      catchError<unknown, Observable<boolean>>((_err) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetSupportedAispInstitutions' }));
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.supportedAispInstitutionsLoading, false));
        this.alertHandlerService.showAlertFn('error', 'Failed to get supported aisp institutions');
        throw _err;
      }),
    );
  }

  @Action(SetPispDepositInitiateResponse)
  setPispDepositInitiateResponse(
    ctx: StateContext<SavingsAccountStateModel>,
    action: SetPispDepositInitiateResponse,
  ) {
    ctx.patchState({
      pispDepositInitiateResponse: action.response,
    });
  }

  @Action(SetSupportedPispInstitutions)
  setSupportedPispInstitutions(
    ctx: StateContext<SavingsAccountStateModel>,
    action: SetSupportedPispInstitutions,
  ) {
    ctx.patchState({
      pispSupportedInstitutions: action.institutions,
    });
  }

  @Action(GetSupportedPispInstitutions)
  fetchSupportedPispInstitutions(
    ctx: StateContext<SavingsAccountStateModel>,
    action: GetSupportedPispInstitutions,
  ) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'GetSupportedPispInstitutions' }));
    ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.supportedPispInstitutionsLoading, true));
    return this.savingsAccountService.fetchSupportedPispInstitutions(action.country, action.feature).pipe(
      tap((response) => {
        ctx.patchState({
          pispSupportedInstitutions: response,
        });
        if (response) ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetSupportedPispInstitutions' }));
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.supportedAispInstitutionsLoading, false));
      }),
      catchError<unknown, Observable<boolean>>((_err) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetSupportedPispInstitutions' }));
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.supportedAispInstitutionsLoading, false));
        throw _err;
      }),
    );
  }

  @Action(GetNominatedAccountDetails)
  fetchNominatedAccountDetails(
    ctx: StateContext<SavingsAccountStateModel>,
    action: GetNominatedAccountDetails,
  ) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'GetNominatedAccountDetails' }));
    return this.savingsAccountService.fetchNominatedAccountInformation(action.accountId).pipe(
      tap((response) => {
        ctx.patchState({
          nominatedAccount: response,
        });
        if (response) ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetNominatedAccountDetails' }));
      }),
    );
  }

  @Action(UpdateInternalTransferFromAccountId)
  updateInternalTransferFromAccounts(
    ctx: StateContext<SavingsAccountStateModel>,
    action: UpdateInternalTransferFromAccountId,
  ) {
    ctx.patchState({
      ...ctx.getState(),
      internalTransferFromAccountId: action.accountId,
    });
  }

  @Action(UpdateInternalTransferToAccountId)
  updateInternalTransferToAccounts(
    ctx: StateContext<SavingsAccountStateModel>,
    action: UpdateInternalTransferToAccountId,
  ) {
    ctx.patchState({
      ...ctx.getState(),
      internalTransferToAccountId: action.accountId,
    });
  }

  @Action(GetSavingAccountDocumentDetails)
  getSavingAccountDocumentDetails(
    ctx: StateContext<SavingsAccountStateModel>,
    action: GetSavingAccountDocumentDetails,
  ) {
    ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentsList, true));
    return this.documentService
      .getDocumentFiles(action.documentContext, action.accountId, action.documentCategory)
      .pipe(
        tap((response) => {
          ctx.patchState({
            ...ctx.getState(),
            documentDetails: response,
          });
          ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentsList, false));
        }),
        catchError((_err) => {
          ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.documentsList, false));
          throw _err;
        }),
      );
  }

  @Action(GetAccountRequests)
  fetchAccountRequests(ctx: StateContext<SavingsAccountStateModel>) {
    ctx.dispatch(new AddProgressBarStack({ uniqueId: 'GetAccountRequests' }));
    return this.savingsAccountService.getAccountRequests().pipe(
      tap((response: AccountOpenRequestType[]) => {
        const prevState = ctx.getState();
        //trim sensitive extra attributes before storing in state
        const updatedResponse = response.map((request) => {
          if (request.additionalDetails?.savings?.extraAttributes)
            delete request.additionalDetails.savings.extraAttributes;
          return request;
        });
        ctx.patchState({
          ...ctx.getState(),
          totalAccountRequests: response.length,
          accountRequests: updatedResponse,
        });
        if (response) ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetAccountRequests' }));
        if (prevState.totalAccountRequests !== response.length) {
          this.hideConditionsService.setHideConditionFromWidget(
            WidgetsHideConditionsSelector.SAVINGS_ACCOUNT_REQUESTS,
          );
        }
      }),
      catchError((_err) => {
        ctx.dispatch(new RemoveProgressBarStack({ uniqueId: 'GetAccountRequests' }));
        throw _err;
      }),
    );
  }

  @Action(CreateSavingsAccount)
  createSavingsAccount(ctx: StateContext<SavingsAccountStateModel>, action: CreateSavingsAccount) {
    return this.savingsAccountService.createSavingsAccount(action.payload).pipe(
      tap((response) => {
        ctx.patchState({
          latestAccountRequest: response.data,
        });
      }),
      catchError((_err) => {
        throw _err;
      }),
    );
  }

  @Action(RefreshActiveAccountDetails)
  fetchActiveAccountDetails(
    ctx: StateContext<SavingsAccountStateModel>,
    action: RefreshActiveAccountDetails,
  ) {
    ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getSavingsAccountDetails, true));
    return this.savingsAccountService.getActiveSavingAccountDetails(action.accountId).pipe(
      tap((response) => {
        const state = ctx.getState();
        const activeAccountIndex = state.accounts.findIndex(
          (account) => account.accountId === action.accountId,
        );
        if (activeAccountIndex !== -1) {
          const updatedAccounts = [...state.accounts];
          updatedAccounts[activeAccountIndex] = response;
          ctx.patchState({
            accounts: updatedAccounts,
          });
        }
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getSavingsAccountDetails, false));
      }),
      catchError((_err) => {
        ctx.dispatch(new UpdateApiIsLoadingAction(ApiActions.getSavingsAccountDetails, false));
        throw _err;
      }),
    );
  }
}
